/**
 * playerAgent — normalise the backend's agent/agency field for display
 * (SE-5494).
 *
 * `PlayerBioData.playerAgent` is a nullable String, but the ingest pipeline
 * additionally emits the literal sentinel `"no agent"` to mean "this player has
 * no agent on record". It is a VALUE, not a null, so every naive
 * `playerAgent ?? "—"` renders the words "no agent" straight at the user. The
 * player-profile header already filters it inline
 * (`src/components/organisms/HeaderPlayer/HeaderPlayerContainer.tsx:83`); this
 * module is that guard promoted to a named, unit-tested seam so the drawer —
 * and any later surface — cannot forget it.
 *
 * Matching is trimmed and case-insensitive: the sentinel has been observed
 * lowercase, but a capitalised variant is the same "no data" signal and must
 * never reach the UI either. Everything else passes through with surrounding
 * whitespace trimmed.
 * @module features/player-kanban/utils/playerAgent
 */

/**
 * The backend's null-equivalent sentinel for "no agent on record", compared
 * case-insensitively after trimming.
 */
export const NO_AGENT_SENTINEL = "no agent";

/**
 * Resolve a displayable agent/agency name.
 * @remarks
 * Returns `undefined` — never a placeholder string — so the caller decides how
 * an absent agent renders (omit the row, show an em dash, …) rather than having
 * that decision baked in here.
 * @param rawAgent - The raw `bioData.playerAgent` value.
 * @returns The trimmed agent name, or `undefined` when absent/blank/sentinel.
 */
export const resolvePlayerAgent = (
  rawAgent: string | null | undefined
): string | undefined => {
  const trimmed = rawAgent?.trim();
  if (!trimmed) return undefined;
  if (trimmed.toLowerCase() === NO_AGENT_SENTINEL) return undefined;
  return trimmed;
};
